'use client'

import { createClient } from '@/lib/supabase/client'
import type { YearlyReviewResponses } from './hooks/use-review-persistence'

// Helper to call Edge Function
async function callEdgeFunction(
  action: string,
  body: any,
  requireAuth: boolean = false
) {
  const supabase = createClient()
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
  
  // Always try to get session and send auth header if available
  // This allows Edge Function to handle both authenticated and anonymous requests
  let authHeader: string | null = null
  
  // Only attempt to get session if we need auth OR if we want to send it optionally
  // For anonymous operations, we can skip session check entirely to avoid errors
  if (requireAuth) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      if (session?.access_token) {
        authHeader = `Bearer ${session.access_token}`
      } else {
        // If auth is required but no session found, check if user is authenticated
        // This handles cases where session might not be in storage yet
        const { data: { user }, error: userError } = await supabase.auth.getUser()
        
        if (userError || !user) {
          throw new Error('Authentication required. Please sign in to continue.')
        }
        
        // User exists but no session - try to get session again after a brief delay
        // This can happen right after signup when session is being established
        await new Promise(resolve => setTimeout(resolve, 100))
        const { data: { session: retrySession } } = await supabase.auth.getSession()
        
        if (retrySession?.access_token) {
          authHeader = `Bearer ${retrySession.access_token}`
        } else {
          throw new Error('Authentication required. Please sign in to continue.')
        }
      }
    } catch (error) {
      // For required auth, we must throw
      throw error instanceof Error ? error : new Error('Authentication required. Please sign in to continue.')
    }
  } else {
    // For optional auth, try to get session but don't fail if it errors
    // This allows anonymous users to use the service without errors
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      
      // Only use session if we got one and no error occurred
      // Errors are expected for unauthenticated users
      if (!sessionError && session?.access_token) {
        authHeader = `Bearer ${session.access_token}`
      }
    } catch (error) {
      // Silently continue without auth header for anonymous operations
      // This is expected behavior for unauthenticated users
      // Errors from getSession() are normal when no session exists
    }
  }

  const functionUrl = `${supabaseUrl}/functions/v1/yearly-review?action=${action}`
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
  }
  
  if (authHeader) {
    headers['Authorization'] = authHeader
  }

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))

    // Handle 401/400 errors gracefully for anonymous users
    if (response.status === 401 || response.status === 400) {
      // Return a structured error instead of throwing
      return { data: null, error: errorData.error || errorData.message || 'Authentication or email required' }
    }
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
  }

  return response.json()
}

// Save review (draft or completed)
export async function saveYearlyReview(
  email: string,
  reviewYear: number,
  responses: YearlyReviewResponses,
  status: 'draft' | 'completed' = 'draft',
  source: 'lead_magnet' | 'planner' | 'direct' = 'lead_magnet'
) {
  // Validate email before sending
  const trimmedEmail = email?.trim()
  if (!trimmedEmail || typeof trimmedEmail !== 'string' || !trimmedEmail.includes('@')) {
    return { success: false, error: 'Valid email is required' }
  }

  // Validate reviewYear
  if (!reviewYear || typeof reviewYear !== 'number' || reviewYear < 2000 || reviewYear > 2100) {
    return { success: false, error: 'Valid review year is required' }
  }

  const result = await callEdgeFunction('save', {
    email: trimmedEmail,
    reviewYear,
    responses: responses || {},
    status,
    source,
  }, false) // Auth optional (works for anonymous users)

  // Ensure consistent response format
  if (result?.error) {
    return { success: false, error: result.error }
  }

  // Edge Function returns { success: true, reviewId, accessToken }
  return result
}

// Load review by email or user
export async function loadYearlyReview(
  email?: string,
  reviewYear?: number,
  code?: string
) {
  return callEdgeFunction('load', {
    email,
    reviewYear,
    code,
  }, false) // Auth optional
}

// Link anonymous review to user account (called on signup)
export async function linkReviewToAccount(userId: string, email: string) {
  // This requires auth, but we pass userId in body for the Edge Function
  // The Edge Function will verify the auth matches the userId
  return callEdgeFunction('link-review', {
    email,
  }, true)
}

// Create share link with referral code
export async function createShareLink(
  email?: string,
  userId?: string
) {
  return callEdgeFunction('create-share-link', {
    email,
  }, false) // Auth optional (works for anonymous users)
}

// Track share event
export async function trackShare(
  referralCode: string,
  platform: 'twitter' | 'facebook' | 'linkedin' | 'copy'
) {
  return callEdgeFunction('track-share', {
    referralCode,
    platform,
  }, false) // No auth required
}

// Get share stats for authenticated user
export async function getShareStats(userId: string) {
  return callEdgeFunction('get-share-stats', {}, true)
}

// Handle referral on signup (called from sign-up page)
export async function handleReferralOnSignup(
  userId: string,
  email: string,
  referralCode?: string | null
) {
  if (!referralCode) {
    return { success: false, message: 'No referral code' }
  }

  return callEdgeFunction('handle-referral', {
    referralCode,
    email,
  }, true)
}

// Link anonymous share to user account and process pending referrals
export async function linkShareToUser(userId: string, email: string) {
  return callEdgeFunction('link-share', {
    email,
  }, true)
}

// Generate review access link (for anonymous users)
export async function getReviewAccessLink(
  email: string,
  reviewYear: number
) {
  return callEdgeFunction('get-access-link', {
    email,
    reviewYear,
  }, false) // Auth optional
}

